import { z } from 'zod';
import { DECISIONS, routeDefinitionSchema, STAGE_MODES, type Decision, type InstanceStatus, type RouteDefinition } from './workflow.js';
import type { LaunchCheckResultDto } from './files.js';

// ---- Справочники ----

export const TASK_KINDS = ['document', 'assignment'] as const;
export type TaskKind = (typeof TASK_KINDS)[number];

export const TASK_KIND_LABELS: Record<TaskKind, string> = {
  document: 'Документ на согласование',
  assignment: 'Поручение',
};

export const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type Priority = (typeof PRIORITIES)[number];
export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Низкий',
  normal: 'Обычный',
  high: 'Высокий',
  urgent: 'Срочно',
};

export type DocumentStatus = 'draft' | 'in_approval' | InstanceStatusExceptProgress;
type InstanceStatusExceptProgress = Exclude<InstanceStatus, 'in_progress'>;
export type AssignmentStatus = 'open' | 'answered' | 'done' | 'cancelled';
export type TaskStatus = DocumentStatus | AssignmentStatus;

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  draft: 'Черновик',
  in_approval: 'На согласовании',
  approved: 'Согласовано',
  returned: 'Не согласовано',
  rejected: 'Отклонено',
  cancelled: 'Прекращено',
  open: 'В работе',
  answered: 'Ждёт проверки',
  done: 'Выполнено',
};

export const STEP_STATUS_LABELS: Record<string, string> = {
  waiting: 'Ожидает очереди',
  pending: 'Решает',
  approved: 'Согласовал',
  declined: 'Не согласовал',
  rejected: 'Отклонил',
  skipped: 'Снят',
  interrupted: 'Остановлен',
};

// ---- Ввод ----

const title = z.string().trim().min(1, 'Укажите название').max(500);
const text = z.string().trim().max(20_000);
const due = z.iso.datetime({ offset: true }).nullable();

/** Поля заявки: простые значения (для условий маршрута и участников из полей). */
const cell = z.union([z.string().max(2000), z.number(), z.null()]);
export const taskFieldsSchema = z
  .record(
    z.string().regex(/^[a-z0-9_-]{1,64}$/i),
    z.union([z.string().max(20_000), z.number(), z.boolean(), z.null(), z.array(z.record(z.string().max(64), cell)).max(200)]),
  )
  .refine((v) => Object.keys(v).length <= 100, 'Слишком много полей');

// ---- Свой маршрут ----

/**
 * Маршрут, который автор собирает сам для одного документа (если тип заявки это разрешает):
 * этапы «одновременно» или «по очереди», в этапах — конкретные сотрудники, только согласование.
 */
export const customRouteSchema = z.object({
  stages: z
    .array(
      z.object({
        name: z.string().trim().min(1, 'Назовите этап').max(200),
        mode: z.enum(STAGE_MODES).default('parallel'),
        userIds: z
          .array(z.uuid('Выберите сотрудника'))
          .min(1, 'Добавьте в этап хотя бы одного сотрудника')
          .max(50)
          .refine((ids) => new Set(ids).size === ids.length, 'Сотрудник указан в этапе дважды'),
        /** Срок каждого участника этапа, часов; пусто — без срока. */
        deadlineHours: z.number().int().min(1).max(24 * 90).nullable().default(null),
        instruction: z.string().trim().max(2000).nullable().default(null),
      }),
    )
    .min(1, 'Добавьте хотя бы один этап')
    .max(20),
});
export type CustomRoute = z.output<typeof customRouteSchema>;
export type CustomRouteInput = z.input<typeof customRouteSchema>;

export const CUSTOM_ROUTE_NAME = 'Свой маршрут';

/** Свой маршрут → описание маршрута для движка согласования. */
export function customRouteDefinition(route: CustomRoute): RouteDefinition {
  return routeDefinitionSchema.parse({
    stages: route.stages.map((st, i) => ({
      key: `custom-${i + 1}`,
      name: st.name,
      mode: st.mode,
      steps: st.userIds.map((userId, j) => ({
        key: `custom-${i + 1}-${j + 1}`,
        participant: { type: 'user', userId },
        kind: 'approve',
        ...(st.deadlineHours ? { deadlineHours: st.deadlineHours } : {}),
        ...(st.instruction ? { instruction: st.instruction } : {}),
      })),
      onComplete: [],
    })),
  });
}

export const createTaskSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('document'),
    /** Пусто — название соберётся по шаблону типа заявки (маршрут, поля, дата). */
    title: z.string().trim().max(500).default(''),
    description: text.optional(),
    priority: z.enum(PRIORITIES).default('normal'),
    dueAt: due.optional(),
    routeId: z.uuid().nullable().optional(),
    /** Свой маршрут вместо маршрута из справочника (если тип заявки это разрешает). */
    customRoute: customRouteSchema.nullable().optional(),
    /** Тип заявки: командировка, отпуск, договор… Поля и маршрут по умолчанию берутся из него. */
    requestTypeId: z.uuid().nullable().optional(),
    fields: taskFieldsSchema.default({}),
  }),
  z.object({
    kind: z.literal('assignment'),
    title,
    description: text.optional(),
    priority: z.enum(PRIORITIES).default('normal'),
    dueAt: due.optional(),
    assigneeId: z.uuid(),
    /** Подзадача: родительская задача и, если ставит согласующий, его шаг. */
    parentId: z.uuid().optional(),
    parentStepId: z.string().max(64).optional(),
  }),
]);
export type CreateTaskInput = z.input<typeof createTaskSchema>;

export const updateTaskSchema = z
  .object({
    title,
    description: text.nullable(),
    priority: z.enum(PRIORITIES),
    dueAt: due,
    routeId: z.uuid().nullable(),
    customRoute: customRouteSchema.nullable(),
    fields: taskFieldsSchema,
  })
  .partial();
export type UpdateTaskInput = z.input<typeof updateTaskSchema>;

export const launchSchema = z.object({
  extraSteps: z
    .array(
      z.object({
        stageKey: z.string().max(64),
        userId: z.uuid(),
        instruction: z.string().trim().max(2000).optional(),
        deadlineHours: z.number().int().min(1).max(24 * 90).optional(),
      }),
    )
    .max(50)
    .default([]),
  extraObservers: z.array(z.uuid()).max(50).default([]),
});
export type LaunchInput = z.input<typeof launchSchema>;

export const decisionSchema = z
  .object({
    decision: z.enum(DECISIONS),
    comment: z.string().trim().max(5000).optional(),
    /** Значения полей, которые шаг требует заполнить. */
    fields: z.record(z.string(), z.unknown()).optional(),
  })
  .refine((v) => v.decision === 'approve' || Boolean(v.comment), {
    message: 'Укажите причину',
    path: ['comment'],
  });
export type DecisionInput = z.input<typeof decisionSchema>;

export const commentSchema = z.object({ body: z.string().trim().min(1, 'Пустой комментарий').max(10_000) });

export const answerSchema = z.object({ answer: z.string().trim().min(1, 'Опишите результат').max(20_000) });

export const reviewSchema = z
  .object({ accept: z.boolean(), comment: z.string().trim().max(5000).optional() })
  .refine((v) => v.accept || Boolean(v.comment), { message: 'Напишите, что доработать', path: ['comment'] });

export const reasonSchema = z.object({ comment: z.string().trim().max(5000).optional() });

export const TASK_SCOPES = ['inbox', 'mine', 'assigned', 'involved', 'all'] as const;
export type TaskScope = (typeof TASK_SCOPES)[number];

/** Сортировка списка задач по колонке. Без неё — «Ждут меня» по срокам, остальные — по последним изменениям. */
export const TASK_SORTS = ['created', 'number', 'title', 'author', 'status', 'due'] as const;
export type TaskSort = (typeof TASK_SORTS)[number];

export const listTasksQuerySchema = z.object({
  scope: z.enum(TASK_SCOPES).default('involved'),
  search: z.string().trim().max(200).optional(),
  status: z.string().max(32).optional(),
  /** Часть ФИО автора. */
  author: z.string().trim().max(200).optional(),
  sort: z.enum(TASK_SORTS).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
  kind: z.enum(TASK_KINDS).optional(),
  /** Ключ типа заявки, например trip; несколько — через запятую. */
  requestType: z.string().regex(/^[a-zA-Z0-9_]{1,64}(,[a-zA-Z0-9_]{1,64}){0,19}$/).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListTasksQuery = z.input<typeof listTasksQuerySchema>;

/**
 * Документы для поля «Связанный документ»: по умолчанию — где пользователь участвует;
 * all — все не черновики видов, заданных у поля («показать все договоры»).
 */
export const linkableQuerySchema = z.object({
  requestTypeId: z.uuid(),
  field: z.string().regex(/^[a-zA-Z0-9_]{1,64}$/),
  all: z.enum(['true', 'false']).default('false').transform((v) => v === 'true'),
  search: z.string().trim().max(200).optional(),
});

// Администратор
export const replaceParticipantSchema = z.object({ userId: z.uuid() });
export const addStepSchema = z.object({
  userId: z.uuid(),
  instruction: z.string().trim().max(2000).optional(),
  deadlineHours: z.number().int().min(1).max(24 * 90).optional(),
});
export const overrideSchema = z
  .object({ decision: z.enum(DECISIONS), comment: z.string().trim().min(1, 'Укажите основание').max(5000) });

// Маршруты
export const routeInputSchema = z.object({
  name: z.string().trim().min(1, 'Укажите название').max(200),
  description: z.string().trim().max(2000).nullable().optional(),
  isActive: z.boolean().default(true),
  definition: routeDefinitionSchema,
});
export type RouteInput = z.input<typeof routeInputSchema>;

// ---- Ответы API ----

export interface UserRef {
  id: string;
  fullName: string;
  position: string | null;
}

export interface RouteDto {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  definition: z.output<typeof routeDefinitionSchema>;
  updatedAt: string;
}

export interface StepDto {
  id: string;
  user: UserRef | null;
  status: string;
  origin: string;
  instruction: string | null;
  activatedAt: string | null;
  dueAt: string | null;
  decidedAt: string | null;
  decidedBy: UserRef | null;
  comment: string | null;
  /** Когда участник в последний раз открывал задачу (для «прочитано»). */
  seenAt: string | null;
  /** Подзадачи, поставленные этим согласующим, — с ответом и историей. */
  subtasks: SubtaskDto[];
  /** Согласование или задача («вложить скан», «зарегистрировать»). */
  kind: 'approve' | 'task';
  /** Шаг из отдела или группы: «любой один из отдела «Юристы»». */
  sourceLabel: string | null;
  /** Шаги «любой один» — один пул: решил один, у остальных снимается. */
  poolId: string | null;
  /** Кто контролирует выполнение. */
  controller: UserRef | null;
  /** Задача: надпись на кнопке и требования к выполнению. */
  actionLabel: string | null;
  requireFile: 'document' | 'scan' | 'attachment' | null;
  requireComment: boolean;
  /** Поля заявки, которые участник заполняет на этом шаге. */
  fillFields: string[];
}

export interface StageDto {
  id: string;
  key: string;
  name: string;
  mode: 'parallel' | 'sequential';
  status: string;
  steps: StepDto[];
}

export interface RoundDto {
  round: number;
  steps: Array<
    Omit<StepDto, 'subtasks' | 'seenAt' | 'sourceLabel' | 'poolId' | 'controller' | 'actionLabel' | 'requireFile' | 'requireComment' | 'fillFields'> & { stageName: string }
  >;
}

export interface ApprovalDto {
  routeName: string;
  status: InstanceStatus;
  round: number;
  stages: StageDto[];
  observers: UserRef[];
  /** Прошлые круги (завершённые), от последнего к первому. */
  pastRounds: RoundDto[];
}

export interface TaskSummaryDto {
  id: string;
  number: number;
  kind: TaskKind;
  requestTypeName?: string | null;
  title: string;
  status: TaskStatus;
  priority: Priority;
  author: UserRef;
  assignee: UserRef | null;
  dueAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Для очереди: шаг, который ждёт решения текущего пользователя. */
  pendingStepId?: string | null;
  /** Срок этого шага (из маршрута). */
  pendingStepDueAt?: string | null;
  /** Подзадача: основная задача, в которой она открывается. */
  root?: { id: string; title: string } | null;
}

/** Подзадача целиком: показывается и решается внутри карточки основной задачи. */
export interface SubtaskDto extends TaskSummaryDto {
  description: string | null;
  answer: string | null;
  answeredAt: string | null;
  closedAt: string | null;
  parentId: string;
  /** Шаг согласования, по которому поставлена; null — общая подзадача. */
  parentStepId: string | null;
  assigneeSeenAt: string | null;
  /** Ход выполнения: системные записи и комментарии. */
  history: CommentDto[];
  can: { answer: boolean; review: boolean; cancel: boolean };
}

export interface CommentDto {
  id: string;
  kind: 'comment' | 'system';
  author: UserRef | null;
  body: string;
  createdAt: string;
}

export interface TaskPermissionsDto {
  edit: boolean;
  launch: boolean;
  restart: boolean;
  cancel: boolean;
  decide: string | null;
  createSubtask: boolean;
  /** Блок «Подзадачи» со всеми подзадачами задачи (право «Создание подзадач в любой задаче»). */
  manageSubtasks: boolean;
  /** Удалить навсегда: свой черновик или любую задачу с правом «Удаление задач и маршрутов». */
  delete: boolean;
  answer: boolean;
  review: boolean;
  override: boolean;
}

export interface TaskDetailDto extends TaskSummaryDto {
  description: string | null;
  /** Задача создана регулярной задачей по расписанию (или подзадача такой задачи). */
  regulation: { id: string; name: string; period: string } | null;
  /** Тип заявки с описанием полей; null — задача без типа (старые и поручения). */
  requestType: {
    id: string;
    name: string;
    behavior: 'generic' | 'trip' | 'vacation';
    fields: import('./requests.js').FieldDefinition[];
    computed: Array<{ key: string; label: string }>;
    allowRouteChoice: boolean;
    allowCustomRoute: boolean;
    /** Шаблоны, которые можно сформировать по кнопке. */
    templates: Array<{ id: string; name: string }>;
  } | null;
  /** За кого заявка (сотрудник в командировке или отпуске). */
  subject: UserRef | null;
  /** Подписи для значений полей: ФИО, названия отделов и документов. */
  fieldLabels: Record<string, string>;
  fields: Record<string, unknown>;
  routeId: string | null;
  /** Свой маршрут документа (вместо маршрута из справочника). */
  customRoute: CustomRoute | null;
  parent: { id: string; number: number; title: string } | null;
  parentStepId: string | null;
  answer: string | null;
  answeredAt: string | null;
  closedAt: string | null;
  assigneeSeenAt: string | null;
  approval: ApprovalDto | null;
  /** Все подзадачи задачи (и по шагам согласования, и общие). */
  subtasks: SubtaskDto[];
  can: TaskPermissionsDto;
}

export interface PreviewDto {
  /** Результаты проверок перед запуском (скан, связанный договор). */
  checks: LaunchCheckResultDto[];
  stages: Array<{ key: string; name: string; mode: 'parallel' | 'sequential'; applies: boolean; steps: Array<{ key: string; user: UserRef | null; instruction: string | null; deadlineHours: number | null; kind: 'approve' | 'task'; sourceLabel: string | null; poolId: string | null; controller: UserRef | null }> }>;
  observers: UserRef[];
  unresolved: Array<{ stageKey: string; stepKey: string }>;
}

export interface InboxCountsDto {
  approvals: number;
  assignments: number;
  reviews: number;
}

export type { Decision };
